/**
 * WalkInIntro: the pre-workflow narrative slideshow for the Walk-in demo.
 *
 * Five real photos anchor the scene so the promoter isn't demonstrating
 * "customer walks in" as a wall of text.  After the last frame, the
 * promoter taps "Start handling customer" to transition into the actual
 * ConfirmPH Business workflow (desktop shell → Notepad → Draft → Booking).
 *
 * Integrates with the outer Auto Play state: while `autoPlay` is on we
 * advance automatically; user can pause / skip / step through manually.
 */

import { useEffect } from "react";
import { ChevronRight, SkipForward, PlayCircle } from "lucide-react";
import { DemoBadge } from "../DemoPanels";

const AUTO_ADVANCE_MS = 3800;

export default function WalkInIntro({
  frames, step, setStep, autoPlay, onFinish, resetToken,
}) {
  const total = frames.length;
  const current = frames[Math.min(step, total - 1)];
  const isLast = step >= total - 1;

  // Reset to first frame whenever the scenario resets.
  useEffect(() => { setStep(0); }, [resetToken, setStep]);

  // Auto-advance while Auto Play is on.  When we reach the last frame we
  // dwell a little longer, then hand off to the desktop workflow.
  useEffect(() => {
    if (!autoPlay) return;
    const t = setTimeout(() => {
      if (isLast) onFinish();
      else setStep(step + 1);
    }, AUTO_ADVANCE_MS);
    return () => clearTimeout(t);
  }, [autoPlay, step, isLast, onFinish, setStep]);

  return (
    <div data-testid="walkin-intro"
         className="mx-auto w-full max-w-[1180px] rounded-2xl bg-slate-900 shadow-2xl overflow-hidden border-[6px] border-slate-800">
      {/* Small chrome so it still feels grounded */}
      <div className="flex items-center justify-between px-4 py-1.5 bg-slate-800 text-slate-300 text-[11px] font-mono">
        <span>Scene · Isla Drive Car Rental · Dumaguete</span>
        <DemoBadge>DEMO</DemoBadge>
      </div>

      <div className="relative bg-black">
        <img src={current.img} alt={current.alt || current.caption}
             data-testid={`walkin-intro-frame-${step}`}
             className="w-full aspect-[16/9] object-cover object-center select-none"
             draggable={false} />
        {/* Caption overlay */}
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/60 to-transparent p-4 sm:p-5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-300 font-semibold mb-1">
            Step {step + 1} of {total}
          </div>
          <div className="text-white text-sm sm:text-base font-medium leading-snug max-w-3xl"
                data-testid="walkin-intro-caption">
            {current.caption}
          </div>
        </div>
      </div>

      <div className="bg-white px-4 py-3 flex items-center gap-2 flex-wrap">
        {/* Progress dots */}
        <div className="flex items-center gap-1.5" data-testid="walkin-intro-progress">
          {frames.map((_, i) => (
            <span key={i}
                  className={`h-1.5 rounded-full transition-all ${
                    i === step ? "w-6 bg-emerald-600"
                    : i < step ? "w-3 bg-emerald-300"
                    : "w-3 bg-slate-200"
                  }`} />
          ))}
        </div>
        <div className="flex-1" />
        <button onClick={onFinish}
                data-testid="walkin-intro-skip"
                className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 px-2 py-1">
          <SkipForward className="w-3.5 h-3.5" /> Skip to workflow
        </button>
        {isLast ? (
          <button onClick={onFinish}
                  data-testid="walkin-intro-start-workflow"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#1B4332] text-white text-sm font-medium hover:bg-emerald-900">
            <PlayCircle className="w-4 h-4" /> Start handling customer
          </button>
        ) : (
          <button onClick={() => setStep(step + 1)}
                  data-testid="walkin-intro-next"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#1B4332] text-white text-sm font-medium hover:bg-emerald-900">
            Next <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
